import { Picture } from "@/components/media/Picture";
import { createFileRoute, Link } from "@tanstack/react-router";

import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { PageHero } from "@/components/layout/PageHero";
import { heroFor } from "@/data/heroes";
import { TeamGrid } from "@/components/sections/TeamGrid";
import { pageBlocks } from "@/lib/content";

const TITLE = "About Us — Light of Life International";
const DESCRIPTION =
  "Meet the team and the calling behind Light of Life International, an evangelistic and compassion-driven ministry serving Latin America and Africa.";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://loli.ardent.dev/about" },
    ],
    links: [{ rel: "canonical", href: "https://loli.ardent.dev/about" }],
  }),
  component: AboutPage,
});

const teamTeasers: Record<string, string> = {
  "stephen-evans": "Stephen was born in Australia and grew up in Texas.",
  "alisa-evans": "Alisa was part of the LOLI family before there even was one!",
  "steve-new": "Steve has been part of the LOLI family from the beginning.",
  "dennis-aplicano": "Dennis has been part of the LOLI family from the beginning.",
  "alvaro-matehuala": "Alvaro has been part of the LOLI family since 2012",
};

function AboutPage() {
  const blocks = pageBlocks("about");
  const text = (i: number) => {
    const block = blocks[i];
    return block && "text" in block ? block.text : "";
  };

  const values = [
    { title: text(4), body: [text(5), text(6)] },
    { title: text(7), body: [text(8)] },
    { title: text(9), body: [text(10)] },
    { title: text(11), body: [text(12), text(13)] },
  ];

  return (
    <div className="min-h-dvh bg-background">
      <Header />

      <PageHero
        title="About Us"
        image={heroFor("/about").image}
        subtitle={text(1)}
        attribution={text(2)}
      />

      <main id="main">
        {/* Core Values — left-aligned heading, two-column values, supporting photo */}
        <section className="section-y">
          <div className="container-site">
            <h2 className="text-5xl text-navy sm:text-6xl">Core Values</h2>
            <div className="mt-12 grid gap-12 lg:grid-cols-[1fr_360px]">
              <div className="grid gap-x-10 gap-y-12 sm:grid-cols-2">
                {values.map((value) => (
                  <article key={value.title}>
                    <h3 className="border-b border-border pb-4 text-3xl font-light normal-case text-navy">
                      {value.title}
                    </h3>
                    {value.body.map((paragraph) => (
                      <p key={paragraph.slice(0, 32)} className="mt-4 text-base text-foreground">
                        {paragraph}
                      </p>
                    ))}
                  </article>
                ))}
              </div>
              <Picture
                src="/media/LOLI_Crusades_22-1024x768.jpg"
                alt="A crusade crowd worshipping at night"
                loading="lazy"
                decoding="async"
                className="h-full w-full rounded-2xl object-cover"
                sizes="100vw"
              />
            </div>
          </div>
        </section>

        {/* Meet The Team */}
        <section className="section-y bg-sage">
          <div className="container-site">
            <h2 className="text-center text-5xl text-sage-foreground sm:text-6xl">Meet The Team</h2>
            <TeamGrid teasers={teamTeasers} />
          </div>
        </section>

        {/* Full-bleed crusade band */}
        <Picture
          src="/media/crusades-loop-background-updated.jpg"
          alt="A stadium crusade crowd worshipping"
          loading="lazy"
          decoding="async"
          className="h-[320px] w-full object-cover"
          sizes="(max-width: 768px) 100vw, 50vw"
          widths={[640, 1024]}
        />

        {/* Our Story + Join Us on the navy field */}
        <section className="section-y bg-navy">
          <div className="container-site">
            <h2 className="text-center text-5xl text-white sm:text-6xl">Our Story</h2>

            <div className="mt-16 grid items-center gap-10 md:grid-cols-2">
              <p className="text-base text-white/90">{text(37)}</p>
              <Picture
                src="/media/LOLI_Events_19-1024x768.jpg"
                alt="Stephen Evans preaching from a podium"
                loading="lazy"
                decoding="async"
                className="w-full rounded-2xl object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
                widths={[640, 1024]}
              />
            </div>

            <div className="mt-16 grid items-center gap-10 md:grid-cols-2">
              <Picture
                src="/media/LOLI_Crusades_22.jpg"
                alt="An evening crusade crowd"
                loading="lazy"
                decoding="async"
                className="w-full rounded-2xl object-cover md:order-1"
                sizes="(max-width: 768px) 100vw, 50vw"
                widths={[640, 1024]}
              />
              <p className="text-base text-white/90 md:order-2">{text(40)}</p>
            </div>

            <div className="mt-24 grid items-center gap-10 md:grid-cols-2">
              <Picture
                src="/media/our-story.jpg"
                alt="Mission team members with students"
                loading="lazy"
                decoding="async"
                className="w-full rounded-2xl object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
                widths={[640, 1024]}
              />
              <div>
                <h2 className="text-4xl text-white sm:text-5xl">Join Us</h2>
                <p className="mt-5 text-base text-white/90">{text(44)}</p>
                <Link
                  to="/contact-us"
                  className="mt-8 inline-block rounded bg-brand px-8 py-3 font-bold text-brand-foreground transition-opacity hover:opacity-90"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
